<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePlayerStore } from '~/stores/player'
import MmIcon from '../base/MmIcon.vue'
import MmMenu from '../base/MmMenu.vue'
import CoverArt from '../music/CoverArt.vue'
import PlayerLike from './PlayerLike.vue'
import PlayerSeek from './PlayerSeek.vue'
import PlayerControls from './PlayerControls.vue'

const props = defineProps<{ immersive?: boolean }>()

const { t } = useI18n()
const player = usePlayerStore()
const audio = ref<HTMLAudioElement | null>(null)
const dock = ref<HTMLElement | null>(null)
const queueOpen = ref(false)
const queueItems = computed(() => player.queue.map(track => ({ id: track.id, label: track.title || t('music.play'), icon: track.id === player.currentTrack?.id ? 'disc' as const : 'music' as const, meta: `${Math.floor((track.metadata?.duration || 0) / 60)}:${String(Math.floor((track.metadata?.duration || 0) % 60)).padStart(2,'0')}` })))
let observer: ResizeObserver | undefined
let measuredHeight = 0
const measure = () => {
  if (!dock.value || !player.visible) return
  const height = Math.ceil(dock.value.getBoundingClientRect().height)
  if (height === measuredHeight) return
  measuredHeight = height
  document.documentElement.style.setProperty('--musicmint-player-height', `${height}px`)
  window.dispatchEvent(new CustomEvent('music-player-visibility-change', { detail: { isVisible: true, height } }))
}
watch(() => [player.visible, props.immersive], async ([visible, immersive]) => {
  await nextTick()
  observer?.disconnect()
  if (visible && !immersive && dock.value) { measure(); observer?.observe(dock.value) }
  else { measuredHeight = 0; document.documentElement.style.setProperty('--musicmint-player-height', '0px') }
})
const shortcut = (event: KeyboardEvent) => {
  if (event.defaultPrevented || event.repeat || event.metaKey || event.ctrlKey || event.altKey || event.code !== 'Space' || !player.visible || !player.currentTrack) return
  if ((event.target as HTMLElement)?.isContentEditable || (event.target as HTMLElement)?.closest('input, textarea, select, button, a, [role="button"], [role="textbox"], [role="slider"], [role="dialog"], [role="menu"]')) return
  event.preventDefault()
  player.toggle()
}
const expand = (lyrics = false) => { player.showLyrics = lyrics; player.nowPlayingOpen = true }
const closePlayer = () => { queueOpen.value = false; player.close() }
watch(() => [player.visible, props.immersive], ([visible, immersive]) => { if (!visible || immersive) queueOpen.value = false }, { flush: 'sync' })
onMounted(() => {
  if (audio.value) player.attach(audio.value)
  if (typeof ResizeObserver !== 'undefined') observer = new ResizeObserver(measure)
  window.addEventListener('resize', measure)
  window.addEventListener('keydown', shortcut)
})
onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('resize', measure)
  window.removeEventListener('keydown', shortcut)
  player.detach()
  document.documentElement.style.removeProperty('--musicmint-player-height')
})
</script>

<template>
  <audio ref="audio" preload="none" hidden />
  <section v-if="!immersive && player.visible && player.currentTrack" ref="dock" class="dock mm-root" :aria-label="t('mm.shell.player')">
    <div class="now-group"><button type="button" class="now" :aria-label="t('t24a.openNowPlaying')" @click="expand()">
      <CoverArt :src="player.currentTrack.image_url" alt="" :hue="player.liveHue" :width="52" :height="52" />
      <span class="now-text"><span class="now-title">{{ player.currentTrack.title }}</span><span class="now-sub">@{{ player.currentTrack.display_name }}</span><span v-if="player.loading" role="status" class="player-status">{{ t('ux.loadingAudio') }}</span><span v-if="player.playbackError" role="alert" class="player-status player-error">{{ t('ux.playbackFailed') }}</span></span>
    </button><PlayerLike class="dock-like" :song="player.currentTrack" small :icon-size="18" /></div>
    <div class="center"><PlayerControls /><PlayerSeek /></div>
    <div class="extra">
      <button v-if="player.lyrics" type="button" class="icon-btn sm" :aria-label="t('music.lyrics')" @click="expand(true)"><MmIcon name="lyrics" :size="18" /></button>
      <MmMenu v-if="queueItems.length" v-model="queueOpen" :title="t('mm.shell.queue')" :label="t('t24a.queue')" :items="queueItems" @select="item => { const track = player.queue.find(track => track.id === item.id); if (track) player.play(track, { surface: 'player' }) }">
        <template #trigger="{ toggle, expanded, id }"><button type="button" class="icon-btn sm" :aria-label="t('t24a.queue')" aria-haspopup="menu" :aria-expanded="expanded" :aria-controls="expanded ? id : undefined" @click="toggle"><MmIcon name="queue" :size="18" /></button></template>
      </MmMenu>
      <button type="button" id="player-volume" class="icon-btn sm" :aria-label="t('mm.shell.volume')" :aria-pressed="player.muted" @click="player.toggleMute"><MmIcon name="volume" :size="18" /></button>
      <input class="vol" :style="{ '--volume': `${(player.muted ? 0 : player.volume) * 100}%` }" type="range" min="0" max="1" step="0.01" :value="player.muted ? 0 : player.volume" aria-labelledby="player-volume" @input="player.setVolume(Number(($event.target as HTMLInputElement).value))">
      <button type="button" class="icon-btn sm" :aria-label="t('t24a.fullScreenPlayer')" @click="expand()"><MmIcon name="expand" :size="18" /></button>
      <button type="button" class="icon-btn dock-close" :aria-label="t('ux.closePlayer')" @click="closePlayer"><MmIcon name="x" :size="20" /></button>
    </div>
    <div class="mini-ctrl"><PlayerLike :song="player.currentTrack" small :icon-size="20" /><PlayerControls mini /><button type="button" class="icon-btn dock-close" :aria-label="t('ux.closePlayer')" @click="closePlayer"><MmIcon name="x" :size="20" /></button></div>
    <div class="progress-line" :style="{ '--p': `${player.duration ? player.currentTime / player.duration * 100 : 0}%` }" aria-hidden="true"><i /></div>
  </section>
</template>

<style scoped>
.dock .extra .dock-close, .dock .mini-ctrl .dock-close { display: grid; place-items: center; width: 44px; min-width: 44px; height: 44px; flex: 0 0 44px; }
.dock-close:focus-visible { outline: 2px solid var(--focus); outline-offset: -2px; }
</style>
